import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CalendarCheck, MapPin, Phone, Loader2, Check, ShieldCheck } from 'lucide-react';
import { api } from '../../lib/api';

// /book/<token> — the patient's quick booking page, opened from the text
// or email the clinic's acceptance triggers. No login: the token is the
// credential for this one referral. Phone-first layout.

const inputClass = 'box-border w-full bg-white border border-[rgba(11,11,20,0.12)] rounded-[12px] px-3.5 py-3 text-[15px] text-[#0b0b14] focus:outline-none focus:border-[#0da7ca] focus:ring-4 focus:ring-[rgba(13,167,202,0.12)]';
const fmtDate = (iso, tz) => new Date(iso).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz || 'America/New_York' });
const fmtSlot = (iso, tz) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz || undefined });

export default function QuickBookPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [type, setType] = useState(null);
  const [providerId, setProviderId] = useState('');
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slot, setSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  // Offered times (076): shown first as one-tap choices; "none of these
  // work" tells the clinic and opens the open-slot picker below.
  const [showPicker, setShowPicker] = useState(false);
  const [declining, setDeclining] = useState(false);
  // Next two weeks as tappable days — iOS Safari renders a bare <input
  // type=date> as an empty box, and a day strip is faster on a phone anyway.
  const days = useMemo(() => Array.from({ length: 14 }, (_, i) => {
    const d = new Date(); d.setHours(12, 0, 0, 0); d.setDate(d.getDate() + i + 1);
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
    return { value: `${y}-${m}-${dd}`, dow: d.toLocaleDateString('en-US', { weekday: 'short' }), day: d.getDate(), mon: d.toLocaleDateString('en-US', { month: 'short' }), weekend: d.getDay() === 0 || d.getDay() === 6 };
  }), []);

  useEffect(() => {
    api.quickBookInfo(token)
      .then((d) => {
        setInfo(d);
        setType(d.types?.find((t) => /consult|new patient|eval|exam/i.test(t.label)) || d.types?.[0] || null);
        setProviderId(d.providers?.[0]?.id || '');
        setDate((cur) => cur || days.find((x) => !x.weekend)?.value || days[0].value); // open on the first weekday
      })
      .catch((e) => setError(e.message || 'This booking link is not valid'));
  }, [token, days]);

  useEffect(() => {
    if (!date || !providerId) { setSlots([]); return; }
    let cancelled = false;
    setSlotsLoading(true); setSlot(null);
    api.quickBookAvailability(token, { provider_id: providerId, date, duration: 30, tz_offset: new Date().getTimezoneOffset() })
      .then((s) => { if (!cancelled) setSlots(Array.isArray(s) ? s : (s.slots || [])); })
      .catch(() => { if (!cancelled) setSlots([]); })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });
    return () => { cancelled = true; };
  }, [token, providerId, date]);

  const book = async () => {
    if (!slot) return;
    setSubmitting(true); setError('');
    try {
      const out = await api.quickBook(token, { provider_id: providerId || null, starts_at: slot.starts_at, ends_at: slot.ends_at, type: type?.key || 'consultation', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      setDone(out);
    } catch (e) {
      setError(e.message || 'Could not book that time');
    } finally { setSubmitting(false); }
  };

  const confirmOffer = async (n) => {
    setSubmitting(true); setError('');
    try {
      const out = await api.quickBook(token, { option: n, type: type?.key || 'consultation', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      setDone(out);
    } catch (e) {
      setError(e.message || 'Could not book that time');
    } finally { setSubmitting(false); }
  };

  const declineOffers = async () => {
    setDeclining(true); setError('');
    try { await api.quickBookDeclineOffers(token); setInfo((i) => ({ ...i, offers: [], offers_declined: true })); }
    catch { /* the picker still works without the clinic being told */ }
    finally { setDeclining(false); setShowPicker(true); }
  };

  const shell = (children) => (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #efeef8 0%, #ecebf6 100%)', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', padding: '28px 16px 56px' }}>
      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <img src="/logo-vertical.svg" alt="MediLink" style={{ height: 34, margin: '0 auto', display: 'block' }} />
        </div>
        {children}
        <p style={{ textAlign: 'center', fontSize: 11, color: 'rgba(11,11,20,0.5)', marginTop: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <ShieldCheck size={12} /> Private link for this visit only. Don't forward it.
        </p>
      </div>
    </div>
  );
  const card = { background: '#fff', borderRadius: 20, padding: '22px 20px', boxShadow: '0 1px 0 rgba(0,0,0,0.02), 0 20px 40px -18px rgba(11,11,20,0.14)' };
  const h1 = { fontFamily: '"Bricolage Grotesque", -apple-system, sans-serif', fontSize: 24, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.15, margin: '0 0 6px', color: '#0b0b14' };
  const muted = { fontSize: 13, color: 'rgba(11,11,20,0.6)', lineHeight: 1.5 };
  const label = { display: 'block', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(11,11,20,0.55)', fontWeight: 600, margin: '18px 0 8px' };

  if (error && !info) return shell(<div style={card}><h1 style={h1}>This link isn't valid</h1><p style={muted}>{error}. Ask the office or your attorney to send a new one.</p></div>);
  if (!info) return shell(<div style={{ ...card, textAlign: 'center', color: 'rgba(11,11,20,0.6)' }}><Loader2 size={18} className="animate-spin" style={{ margin: '0 auto 8px' }} /> Loading…</div>);

  const clinic = info.clinic || {};
  const addr = clinic.location ? [clinic.location.address_line1, clinic.location.city, clinic.location.state].filter(Boolean).join(', ') : null;
  const when = done?.starts_at || info.booked?.starts_at;
  const tz = done?.timezone || info.booked?.timezone;

  if (done || info.status === 'scheduled') {
    return shell(
      <div style={card}>
        <div style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(16,185,129,0.14)', color: '#047857', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}><Check size={22} strokeWidth={2.5} /></div>
        <h1 style={h1}>{done ? "You're booked." : 'Already booked.'}</h1>
        <p style={{ ...muted, fontSize: 15, color: '#0b0b14', fontWeight: 600, margin: '8px 0 4px' }}>{when ? fmtDate(when, tz) : ''}</p>
        <p style={muted}>{clinic.name}{clinic.location?.name ? ` · ${clinic.location.name}` : ''}</p>
        {addr && <p style={{ ...muted, display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 10 }}><MapPin size={14} style={{ flexShrink: 0, marginTop: 2 }} /> {addr}</p>}
        {clinic.phone && <p style={{ ...muted, display: 'flex', gap: 6, alignItems: 'center' }}><Phone size={14} /> {clinic.phone}</p>}
        <p style={{ ...muted, marginTop: 14 }}>A confirmation is on its way by text{info.first_name ? '' : ''}. Need to change it? Call the office.</p>
        {info.portal_url && (
          <a href={info.portal_url} style={{ display: 'inline-block', marginTop: 16, fontSize: 13, fontWeight: 600, color: '#0da7ca' }}>Want to see your appointments and forms online? Set up your portal →</a>
        )}
      </div>,
    );
  }

  if (info.status !== 'accepted') {
    return shell(<div style={card}><h1 style={h1}>Nothing to book right now</h1><p style={muted}>This referral is {info.status}. Your attorney's office can send a new one.</p></div>);
  }

  const offers = Array.isArray(info.offers) ? info.offers : [];
  if (offers.length && !showPicker) {
    return shell(
      <div style={card}>
        <h1 style={h1}>{info.first_name ? `${info.first_name}, confirm` : 'Confirm'} your first visit.</h1>
        <p style={muted}><strong style={{ color: '#0b0b14' }}>{clinic.name}</strong> accepted your referral{info.firm ? ` from ${info.firm}` : ''} and offered these times. Tap one and it's booked on the spot. No account needed.</p>

        <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 14, background: '#f3f7fa', border: '1px solid rgba(11,11,20,0.06)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0b0b14' }}>{clinic.location?.name || clinic.name}</div>
          {addr && <div style={{ ...muted, fontSize: 12, display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 2 }}><MapPin size={12} style={{ flexShrink: 0, marginTop: 2 }} /> {addr}</div>}
          {info.service && <div style={{ ...muted, fontSize: 12, marginTop: 2 }}>{info.service}</div>}
        </div>

        <span style={label}>Offered times</span>
        <div style={{ display: 'grid', gap: 10 }}>
          {offers.map((o, i) => (
            <button key={o.starts_at} type="button" onClick={() => confirmOffer(i + 1)} disabled={submitting} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '14px 16px', borderRadius: 14, border: '1px solid rgba(11,11,20,0.12)', background: '#fff', color: '#0b0b14', cursor: submitting ? 'wait' : 'pointer', textAlign: 'left', opacity: submitting ? 0.6 : 1 }}>
              <span style={{ width: 26, height: 26, borderRadius: 999, background: '#0b0b14', color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 600 }}>{fmtDate(o.starts_at, o.timezone)}</span>
                {o.provider_name && <span style={{ ...muted, fontSize: 12 }}>with {o.provider_name}</span>}
              </span>
              <CalendarCheck size={16} style={{ color: '#0da7ca', flexShrink: 0 }} />
            </button>
          ))}
        </div>

        {error && <p style={{ marginTop: 12, fontSize: 13, color: '#b91c1c', background: 'rgba(220,38,38,0.08)', padding: '10px 12px', borderRadius: 10 }}>{error}</p>}

        <button type="button" onClick={declineOffers} disabled={declining || submitting} style={{ marginTop: 18, width: '100%', padding: '13px', borderRadius: 999, border: '1px solid rgba(11,11,20,0.12)', background: '#fff', color: '#0b0b14', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {declining ? <Loader2 size={15} className="animate-spin" /> : null} None of these work — see other times
        </button>
        <p style={{ ...muted, fontSize: 12, textAlign: 'center', marginTop: 10 }}>Tapping a time books it. You'll get a confirmation by text.</p>
      </div>,
    );
  }

  return shell(
    <div style={card}>
      <h1 style={h1}>{info.first_name ? `${info.first_name}, pick` : 'Pick'} your first visit.</h1>
      <p style={muted}><strong style={{ color: '#0b0b14' }}>{clinic.name}</strong> accepted your referral{info.firm ? ` from ${info.firm}` : ''}. Choose a time below and it's booked on the spot. No account needed.</p>
      {info.offers_declined && <p style={{ ...muted, marginTop: 8, fontSize: 12.5 }}>We let the office know the times they offered don't work. Pick any open time below, or wait for them to text new options.</p>}

      <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 14, background: '#f3f7fa', border: '1px solid rgba(11,11,20,0.06)' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0b0b14' }}>{clinic.location?.name || clinic.name}</div>
        {addr && <div style={{ ...muted, fontSize: 12, display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 2 }}><MapPin size={12} style={{ flexShrink: 0, marginTop: 2 }} /> {addr}</div>}
        {info.service && <div style={{ ...muted, fontSize: 12, marginTop: 2 }}>{info.service}</div>}
      </div>

      {info.types?.length > 1 && (
        <>
          <span style={label}>Type of visit</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {info.types.map((t) => {
              const sel = type?.key === t.key;
              return <button key={t.key} type="button" onClick={() => setType(t)} style={{ padding: '9px 13px', borderRadius: 10, fontSize: 13, fontWeight: 600, border: '1px solid', borderColor: sel ? 'transparent' : 'rgba(11,11,20,0.12)', background: sel ? (t.color || '#0da7ca') : '#fff', color: sel ? '#fff' : '#0b0b14', cursor: 'pointer' }}>{t.label}</button>;
            })}
          </div>
        </>
      )}

      {info.providers?.length > 1 && (
        <>
          <span style={label}>Provider</span>
          <select className={inputClass} value={providerId} onChange={(e) => setProviderId(e.target.value)}>
            {info.providers.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
          </select>
        </>
      )}
      {info.providers?.length === 0 && <p style={{ ...muted, color: '#b45309', marginTop: 12 }}>The clinic hasn't put a provider on the calendar yet. Call them at {clinic.phone || 'the office'} to book.</p>}

      <span style={label}>Pick a day</span>
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '2px 2px 6px', margin: '0 -2px', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
        {days.map((d) => {
          const sel = date === d.value;
          return (
            <button key={d.value} type="button" onClick={() => setDate(d.value)} style={{ flex: '0 0 auto', minWidth: 64, padding: '10px 8px', borderRadius: 12, border: '1px solid', borderColor: sel ? 'transparent' : 'rgba(11,11,20,0.12)', background: sel ? '#0b0b14' : '#fff', color: sel ? '#fff' : d.weekend ? 'rgba(11,11,20,0.45)' : '#0b0b14', cursor: 'pointer', textAlign: 'center', lineHeight: 1.1 }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', opacity: 0.8 }}>{d.dow}</div>
              <div style={{ fontSize: 18, fontWeight: 700, margin: '3px 0 1px' }}>{d.day}</div>
              <div style={{ fontSize: 11, opacity: 0.8 }}>{d.mon}</div>
            </button>
          );
        })}
      </div>

      {date && (
        <>
          <span style={label}>Available times</span>
          {slotsLoading ? (
            <p style={{ ...muted, display: 'flex', gap: 8, alignItems: 'center' }}><Loader2 size={14} className="animate-spin" /> Checking the clinic's calendar…</p>
          ) : slots.length === 0 ? (
            <p style={{ ...muted, padding: '14px', textAlign: 'center', background: '#f3f7fa', borderRadius: 12 }}>No open times that day. Tap another day.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {slots.map((s) => {
                const sel = slot?.starts_at === s.starts_at;
                return <button key={s.starts_at} type="button" onClick={() => setSlot(s)} style={{ padding: '12px 6px', borderRadius: 10, fontSize: 14, fontWeight: 600, border: '1px solid', borderColor: sel ? 'transparent' : 'rgba(11,11,20,0.12)', background: sel ? '#0da7ca' : '#fff', color: sel ? '#fff' : '#0b0b14', cursor: 'pointer' }}>{fmtSlot(s.starts_at)}</button>;
              })}
            </div>
          )}
        </>
      )}

      {error && <p style={{ marginTop: 12, fontSize: 13, color: '#b91c1c', background: 'rgba(220,38,38,0.08)', padding: '10px 12px', borderRadius: 10 }}>{error}</p>}

      <button type="button" onClick={book} disabled={!slot || submitting} style={{ marginTop: 20, width: '100%', padding: '15px', borderRadius: 999, border: 'none', background: '#0b0b14', color: '#fff', fontSize: 15, fontWeight: 600, cursor: slot ? 'pointer' : 'not-allowed', opacity: !slot || submitting ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <CalendarCheck size={16} />} {submitting ? 'Booking…' : slot ? `Book ${fmtSlot(slot.starts_at)}` : 'Pick a time'}
      </button>
      <p style={{ ...muted, fontSize: 12, textAlign: 'center', marginTop: 10 }}>Booked on the spot. You'll get a confirmation by text.</p>
    </div>,
  );
}
